import React, { ReactElement } from "react";
import Link from "next/link";
import moment from "moment";
import * as types from "types";

interface Props {
  cardType: "reference" | "evergreen";
  id: string;
  keywords: types.Keyword[];
  created: string;
}

const CardFooter: React.FC<Props> = ({ cardType, keywords, created }: Props): ReactElement => {
  return (
    <div>
      <div className="px-4">
        {keywords ? <KeywordList cardType={cardType} keywords={keywords} /> : null}
      </div>
      <div className="px-4 flex justify-between items-center">
        <Timestamp created={created} />
        <div className="text-right">
          <LinkedEntryCount fromCount={0} toCount={0} />
        </div>
      </div>
    </div>
  );
};

interface TimestampProps {
  created: string;
}

const Timestamp: React.FC<TimestampProps> = ({ created }: TimestampProps): ReactElement => {
  return (
    <span className="text-xs font-semibold text-gray-700 mr-2">→ {moment(created).fromNow()}</span>
  );
};

interface LinkedEntryCountProps {
  fromCount: number;
  toCount: number;
}

const LinkedEntryCount: React.FC<LinkedEntryCountProps> = ({
  fromCount,
  toCount,
}: LinkedEntryCountProps): ReactElement => {
  let parentText = `${fromCount} parents`;
  if (fromCount === 1) {
    parentText = `${fromCount} parent`;
  }
  let childText = `${toCount} children`;
  if (toCount === 1) {
    childText = `${toCount} child`;
  }
  return (
    <span className="text-xs font-semibold text-gray-700 mr-2">
      {`${parentText} / ${childText}`}
    </span>
  );
};

interface KeywordListProps {
  cardType: "reference" | "evergreen";
  keywords: types.Keyword[];
}

const KeywordList: React.FC<KeywordListProps> = ({
  cardType,
  keywords,
}: KeywordListProps): ReactElement => {
  const linkPath = cardType == "reference" ? "/references" : "/notes";
  return (
    <div>
      {keywords.map((kw, idx) => (
        <span
          className="inline-block bg-gray-400 rounded-full px-3 py-1 text-xs font-semibold text-gray-700 mr-2"
          key={idx}
        >
          <Link href={{ pathname: linkPath, query: { keyword: kw.name } }}>
            <a>{kw.name}</a>
          </Link>
        </span>
      ))}
    </div>
  );
};

export default CardFooter;
